import { useState, useEffect } from "react";
import { Target } from "lucide-react";
import { colors, cardShadow } from "../constants";
import PerformanceMatchCard from "./PerformanceMatchCard";
import { useAuth } from "@/App";

import { BACKEND_URL } from "@/lib/api";

// Rewards Content - Performance Dashboard
const RewardsContent = ({ user, onNavigateToToday }) => {
  const [loading, setLoading] = useState(true);
  const [totalCredits, setTotalCredits] = useState(0);
  const [performanceHistory, setPerformanceHistory] = useState([]);
  const auth = useAuth();

  useEffect(() => {
    fetchPerformanceData();
  }, [user]);

  const fetchPerformanceData = async () => {
    try {
      setLoading(true);
      const userId = user?.user_id || user?.id;
      if (!userId) {
        setLoading(false);
        return;
      }

      const response = auth?.authFetch
        ? await auth.authFetch(`${BACKEND_URL}/api/rewards/performance/${userId}`)
        : await fetch(`${BACKEND_URL}/api/rewards/performance/${userId}`);

      if (response.ok) {
        const data = await response.json();
        setTotalCredits(data.total_credits_earned || 0);
        setPerformanceHistory(data.performance_history || []);
      }
    } catch (_error) {
      // silently ignore
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-[#01ff9a] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="rewards-performance-dashboard">
      {/* Total Hudle Credits Earned Banner */}
      <div
        className="rounded-[20px] p-6 mb-6"
        style={{
          background: colors.cardBg,
          border: `1px solid ${colors.cardBorder}`,
          boxShadow: cardShadow,
        }}
        data-testid="total-credits-banner"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium mb-2" style={{ color: colors.textMuted }}>
              Total Hudle Credits Earned
            </p>
            <span
              className="text-[32px] font-bold leading-none"
              style={{
                background: "linear-gradient(90deg, #01ff9a, #acffe5)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
              data-testid="total-credits-value"
            >
              {totalCredits.toLocaleString()}
            </span>
          </div>
          <button
            onClick={onNavigateToToday}
            className="px-4 py-2.5 rounded-xl text-sm font-semibold transition-all hover:opacity-90"
            style={{
              background: "linear-gradient(90deg, #01ff9a, #acffe5)",
              color: "#0f0f0f",
            }}
            data-testid="earn-more-btn"
          >
            Play Now →
          </button>
        </div>
      </div>

      {/* Your Predictions Section */}
      {performanceHistory.length > 0 && (
        <div>
          <h3
            className="text-sm font-semibold mb-4 uppercase tracking-wide"
            style={{ color: "rgba(255,255,255,0.7)" }}
          >
            Your Predictions
          </h3>
          <div className="flex flex-col" style={{ gap: "20px" }}>
            {performanceHistory.map((prediction, index) => (
              <PerformanceMatchCard
                key={prediction.match_id}
                prediction={prediction}
                matchNumber={prediction.match_number || performanceHistory.length - index}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {performanceHistory.length === 0 && (
        <div
          className="rounded-[20px] p-8 text-center"
          style={{
            background: colors.cardBg,
            border: `1px solid ${colors.cardBorder}`,
          }}
        >
          <Target className="w-12 h-12 mx-auto mb-4" style={{ color: colors.textMuted }} />
          <p className="text-sm font-medium text-white mb-2">No predictions yet</p>
          <p className="text-xs mb-4" style={{ color: colors.textMuted }}>
            Start predicting to earn Hudle Credits!
          </p>
          <button
            onClick={onNavigateToToday}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold transition-all hover:opacity-90"
            style={{
              background: "linear-gradient(90deg, #01ff9a, #acffe5)",
              color: "#0f0f0f",
            }}
          >
            Make your first prediction
          </button>
        </div>
      )}
    </div>
  );
};

export default RewardsContent;
